import { useCompleteThematicMeeting, useReopenThematicMeeting } from '@/api/thematic-meetings';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';

/**
 * Completes and reopens a meeting, from its card as from its plan: each is confirmed by a toast or
 * reported on failure, and resolves `true` once it succeeded.
 */
export const useMeetingStatusActions = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const completeMeeting = useCompleteThematicMeeting();
    const reopenMeeting = useReopenThematicMeeting();

    const run = async (action: () => Promise<unknown>, successMessage: string) => {
        try {
            await action();
            showSuccessToast({ message: successMessage });
            return true;
        } catch (error) {
            handleError({ error });
            return false;
        }
    };

    return {
        markCompleted: (meetingId: string) =>
            run(
                () => completeMeeting.mutateAsync(meetingId),
                t('thematicMeetings.list.markCompletedSuccess'),
            ),
        reopen: (meetingId: string) =>
            run(
                () => reopenMeeting.mutateAsync(meetingId),
                t('thematicMeetings.list.reopenMeetingSuccess'),
            ),
        isCompleting: completeMeeting.isPending,
        isReopening: reopenMeeting.isPending,
    };
};
